import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import { Save, Info } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import useApi from 'hooks/useApi';
import { useContext } from 'react';
import ApiContext from 'contexts/ApiContext';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import { LicenseVerificationEmpty } from 'components/LicenseVerification';
import ImportResourcesDialog from './ImportResourcesDialog';
import { renderSelectSearch } from 'components/SelectSearchField';

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
}

interface ResourceType {
  id: string;
  name: string;
  description: string;
  fields: Field[];
  createdAt: string;
  category?: 'user' | 'others';
  access?: string[];
  invite?: boolean;
  welcomeEmail?: boolean;
  editable?: boolean;
  deletable?: boolean;
  allowRating?: boolean;
}

interface ResourceDataManagerProps {
  selectedResourceType: ResourceType | null;
  onRefresh: () => void;
  onTabChange: () => void;
  clientUID: string | null;
}

// The teams endpoint returns creation order, which reads as unordered once a client
// has more than a handful. Entries come through as objects or bare strings depending
// on the response shape.
const teamSortLabel = (team: any): string =>
  (typeof team === 'string' ? team : team?.name || team?.title || '').trim();

const sortTeamsByName = (list: any[]): any[] =>
  [...list].sort((a, b) => teamSortLabel(a).localeCompare(teamSortLabel(b), undefined, { sensitivity: 'base' }));

const ResourceDataManager: React.FC<ResourceDataManagerProps> = ({
  selectedResourceType,
  onRefresh,
  onTabChange,
  clientUID
}) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [currentResourceType, setCurrentResourceType] = useState<ResourceType | null>(
    selectedResourceType
  );
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Dropdown data state
  const [teams, setTeams] = useState<any[]>([]);
  const [teamSearchText, setTeamSearchText] = useState('');
  const [resourceTypeSearchText, setResourceTypeSearchText] = useState('');
  const [loadingDropdownData, setLoadingDropdownData] = useState(false);

  // Country codes for dropdown
  const countryCodes = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087', '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'
  ];

  const { showToast } = useApi();
  const apiContext = useContext(ApiContext);

  if (!apiContext) {
    throw new Error('ResourceDataManager must be used within an ApiProvider');
  }

  const {
    getResourceTypes,
    createResourceData,
    getTeamByClient,
    uploadAsset
  } = apiContext;

  // Check if user has permission to add/edit resources
  const canEditResource = useHasPermission(PERMISSIONS.ADD_EDIT_RESOURCE);

  const filteredResourceTypes = (Array.isArray(resourceTypes) ? resourceTypes : []).filter(
    (resourceType) =>
      `${resourceType.name} ${resourceType.category || ''}`
        .toLowerCase()
        .includes(resourceTypeSearchText.toLowerCase())
  );

  useEffect(() => {
    loadResourceTypes();
  }, []);

  useEffect(() => {
    if (selectedResourceType) {
      setCurrentResourceType(selectedResourceType);
      resetForm();
      loadDropdownData(selectedResourceType);
    }
  }, [selectedResourceType]);

  // Load dropdown data when clientUID changes
  useEffect(() => {
    if (currentResourceType && clientUID) {
      loadDropdownData(currentResourceType);
    }
  }, [clientUID]);

  const loadDropdownData = async (resourceType: ResourceType) => {
    if (!resourceType || !resourceType.fields || !Array.isArray(resourceType.fields)) {
      console.warn('Invalid resource type for loading dropdown data:', resourceType);
      return;
    }

    const hasDropdownFields = resourceType.fields.some(field => field && field.type === 'dropdown');
    if (!hasDropdownFields) return;

    setLoadingDropdownData(true);
    
    try {
      // Check if we need to load teams
      const needsTeams = resourceType.fields.some(field => 
        field && field.type === 'dropdown' && field.dropdownSource === 'teams'
      );
      
      if (needsTeams) {
        if (!clientUID) {
          console.warn('ClientUID is required to load teams but not provided:', clientUID);
          setTeams([]);
        } else {
          try {
            console.log('Loading teams for clientUID:', clientUID);
            const teamsResponse = await getTeamByClient(clientUID);
            console.log('Teams response:', teamsResponse);
            
            if (teamsResponse?.success && Array.isArray(teamsResponse?.data)) {
              console.log('Teams loaded successfully:', teamsResponse.data);
              setTeams(sortTeamsByName(teamsResponse.data));
            } else if (Array.isArray(teamsResponse)) {
              // Handle case where response is directly an array
              console.log('Teams loaded successfully (direct array):', teamsResponse);
              setTeams(sortTeamsByName(teamsResponse));
            } else {
              console.warn('Failed to load teams, unexpected response format:', teamsResponse);
              setTeams([]);
            }
          } catch (teamError) {
            console.error('Error loading teams:', teamError);
            setTeams([]);
            showToast('Failed to load teams for dropdown', 'error');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    } finally {
      setLoadingDropdownData(false);
    }
  };

  const loadResourceTypes = async () => {
    setInitialLoading(true);
    try {
      const response = await getResourceTypes(clientUID || undefined, null);
      // Ensure response is an array
      if (Array.isArray(response)) {
        setResourceTypes(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setResourceTypes(response.data);
      } else {
        console.error('Invalid resource types response format:', response);
        setResourceTypes([]);
        showToast('Invalid response format for resource types', 'error');
      }
    } catch (error) {
      console.error('Failed to load resource types:', error);
      setResourceTypes([]);
      showToast('Failed to load resource types', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const resetForm = () => {
    if (currentResourceType) {
      const initialData: { [key: string]: any } = {};
      currentResourceType.fields.forEach(field => {
        // For teams dropdown in user resource types, initialize as array
        if (field.name === 'team' && field.type === 'dropdown' && field.dropdownSource === 'teams' && currentResourceType.category === 'user') {
          initialData[field.name] = [];
        } else {
          initialData[field.name] = '';
        }
      });
      setFormData(initialData);
      setTeamSearchText('');
    }
  };

  const handleResourceTypeChange = (resourceTypeId: string) => {
    const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
    setCurrentResourceType(resourceType || null);
    if (resourceType) {
      const initialData: { [key: string]: any } = {};
      resourceType.fields.forEach(field => {
        // For teams dropdown in user resource types, initialize as array
        if (field.name === 'team' && field.type === 'dropdown' && field.dropdownSource === 'teams' && resourceType.category === 'user') {
          initialData[field.name] = [];
        } else {
          initialData[field.name] = '';
        }
      });
      setFormData(initialData);
      // Load dropdown data for the newly selected resource type
      loadDropdownData(resourceType);
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    if (!currentResourceType) {
      showToast('Please select a resource type', 'error');
      return;
    }

    // Validate required fields
    const missingFields = currentResourceType.fields
      .filter(field => {
        if (!field.required) return false;
        
        const value = formData[field.name];
        
        // Handle array values (like multi-select teams)
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        
        // Handle string values
        return !value?.toString().trim();
      })
      .map(field => field.name);

    if (missingFields.length > 0) {
      showToast(`Please fill in required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }

    setLoading(true);
    try {
      const submitData: { [key: string]: any } = {};

      // Upload image files first, then include URLs in the payload
      for (const [key, val] of Object.entries(formData)) {
        if (val instanceof File) {
          try {
            const res = await uploadAsset(val, `resource-images/${currentResourceType.id}`);
            const imgData = res?.data;
            if (imgData) {
              submitData[key] = {
                file_name: imgData.file_name,
                file_path: imgData.file_path,
                mime_type: imgData.mime_type,
                public_url: imgData.public_url,
              };
            }
          } catch (imgErr) {
            console.error(`Failed to upload image for field ${key}:`, imgErr);
            showToast(`Failed to upload image for ${key}`, 'error');
            setLoading(false);
            return;
          }
        } else if (val !== null && val !== undefined) {
          submitData[key] = val;
        }
      }

      if (currentResourceType.category === 'user') {
        const additionalPayload = {
          category: 'user',
          ...submitData
        };
        await createResourceData(currentResourceType.id, submitData, additionalPayload);
      } else {
        await createResourceData(currentResourceType.id, submitData);
      }

      showToast('Resource data added successfully', 'success');
      resetForm();
      onRefresh();
    } catch (error: any) {
      showToast(`${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: Field) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            multiline
            rows={4}
            variant="outlined"
            InputProps={{ readOnly: !canEditResource }}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name}
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            InputProps={{ readOnly: !canEditResource }}
          />
        );

      case 'email':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name}
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            InputProps={{ readOnly: !canEditResource }}
          />
        );

      case 'date':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{ readOnly: !canEditResource }}
          />
        );

      case 'dropdown':
        const getDropdownOptions = () => {
          switch (field.dropdownSource) {
            case 'teams':
              if (!Array.isArray(teams)) {
                console.warn('Teams data is not an array:', teams);
                return [];
              }
              if (teams.length === 0 && !loadingDropdownData) {
                console.warn('No teams available for dropdown. ClientUID:', clientUID);
              }
              return teams.map(team => ({ 
                value: team.uid || team.id, 
                label: team.name || team.title || 'Unknown Team' 
              }));
            case 'countryCode':
              return countryCodes.map(code => ({ value: code, label: code }));
            default:
              console.warn('Unknown dropdown source:', field.dropdownSource);
              return [];
          }
        };

        const allOptions = getDropdownOptions();
        const isTeamsField = field.dropdownSource === 'teams';
        // Clients with a long team list are unusable without a filter.
        const options = isTeamsField
          ? allOptions.filter(opt => String(opt.label).toLowerCase().includes(teamSearchText.toLowerCase()))
          : allOptions;

        // Check if this is a teams field in a user resource type (should be multi-select)
        const isMultiSelectTeams = field.name === 'team' && 
                                   field.dropdownSource === 'teams' && 
                                   currentResourceType?.category === 'user';

        return (
          <FormControl key={field.name} fullWidth variant="outlined">
            <InputLabel>{field.name}</InputLabel>
            <Select
              value={isMultiSelectTeams ? (Array.isArray(value) ? value : []) : value}
              label={field.name}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              required={field.required}
              disabled={loadingDropdownData || !canEditResource}
              multiple={isMultiSelectTeams}
              MenuProps={{
                // Without this the first option takes focus and the search box
                // loses every keystroke.
                disableAutoFocusItem: true,
                PaperProps: { style: { maxHeight: 300 } }
              }}
              renderValue={isMultiSelectTeams ? (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((selectedValue) => {
                    const option = allOptions.find(opt => opt.value === selectedValue);
                    return (
                      <Chip 
                        key={selectedValue} 
                        label={option?.label || selectedValue} 
                        size="small" 
                      />
                    );
                  })}
                </Box>
              ) : undefined}
            >
              {isTeamsField && renderSelectSearch(teamSearchText, setTeamSearchText, 'Search teams...')}

              {loadingDropdownData ? (
                <MenuItem disabled>
                  <em>Loading...</em>
                </MenuItem>
              ) : !field.required && !isMultiSelectTeams ? (
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
              ) : null}
              
              {!loadingDropdownData && options.length === 0 && isTeamsField ? (
                <MenuItem disabled>
                  <em>
                    {teamSearchText
                      ? 'No teams found matching search'
                      : `No teams available${!clientUID ? ' (Client not selected)' : ''}`}
                  </em>
                </MenuItem>
              ) : null}
              
              {!loadingDropdownData && options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'license_verification':
        // Nothing to fill in on creation — the driver scans this in the mobile
        // app and the value is written by the verification callback.
        return (
          <Box key={field.name}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              {field.name}
            </Typography>
            <LicenseVerificationEmpty />
          </Box>
        );

      case 'image': {
        const imgVal = formData[field.name];
        const isFile = imgVal instanceof File;
        const imgPreviewUrl = isFile
          ? URL.createObjectURL(imgVal)
          : (typeof imgVal === 'object' && (imgVal?.signedUrl || imgVal?.public_url))
            ? (imgVal.signedUrl || imgVal.public_url)
            : null;

        return (
          <Box key={field.name} sx={{ border: '1px dashed', borderColor: imgPreviewUrl ? 'success.main' : 'grey.400', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: imgPreviewUrl ? 'success.50' : 'grey.50' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              {field.name} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            {imgPreviewUrl ? (
              <Box sx={{ mb: 1.5 }}>
                <Box
                  component="img"
                  src={imgPreviewUrl}
                  alt={field.name}
                  sx={{ maxWidth: 200, maxHeight: 160, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}
                />
                {isFile && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {imgVal.name}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">No image selected</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button variant="outlined" component="label" size="small" disabled={!canEditResource}>
                {imgPreviewUrl ? 'Replace' : 'Choose Image'}
                <input type="file" hidden accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFieldChange(field.name, f); }} />
              </Button>
              {imgPreviewUrl && (
                <Button variant="text" size="small" color="error" onClick={() => handleFieldChange(field.name, null)}>
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        );
      }

      default: // text
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            variant="outlined"
          />
        );
    }
  };

  if (!currentResourceType && resourceTypes.length === 0) {
    if (initialLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Save sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Resource Types Available
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You need to create resource types first before adding data.
          Resource types define the structure and fields for your data entries.
        </Typography>
        <Button variant="contained" onClick={onTabChange} size="large">
          Create Resource Type
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Add Resource Data
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add new data entries for your resource types.
          </Typography>
        </Box>
        <PermissionWrapper permission={PERMISSIONS.BULK_UPLOAD_RESOURCE} disableInsteadOfHide={true}>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            disabled={!currentResourceType}
          >
            Import
          </Button>
        </PermissionWrapper>
      </Box>

      <ImportResourcesDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        resourceType={currentResourceType}
        clientUID={clientUID}
        onImported={onRefresh}
      />

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Resource Type Selection */}
          <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Resource Type
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Resource Type</InputLabel>
            <Select
              value={currentResourceType?.id || ''}
              label="Resource Type"
              onChange={(e) => handleResourceTypeChange(e.target.value)}
              MenuProps={{
                disableAutoFocusItem: true,
                PaperProps: { style: { maxHeight: 300 } }
              }}
            >
              {renderSelectSearch(resourceTypeSearchText, setResourceTypeSearchText, 'Search resource types...')}

              {filteredResourceTypes.length === 0 && (
                <MenuItem disabled>
                  <em>No resource types found matching search</em>
                </MenuItem>
              )}

              {filteredResourceTypes.map((resourceType) => (
                <MenuItem key={resourceType.id} value={resourceType.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{resourceType.name}</Typography>
                    {resourceType.category && (
                      <Chip 
                        label={resourceType.category} 
                        size="small" 
                        color={resourceType.category === 'user' ? 'primary' : 'default'}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            
            {/* Show resource type configuration info */}
            {currentResourceType && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Info fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="primary">
                    Resource Type Configuration
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {currentResourceType.category && (
                    <Chip 
                      label={`Category: ${currentResourceType.category}`} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                  {currentResourceType.access && currentResourceType.access.length > 0 && (
                    <Chip 
                      label={`Access: ${currentResourceType.access.join(', ')}`} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                  {currentResourceType.invite !== undefined && (
                    <Chip 
                      label={`Invite: ${currentResourceType.invite ? 'Enabled' : 'Disabled'}`} 
                      size="small" 
                      variant="outlined"
                      color={currentResourceType.invite ? 'success' : 'default'}
                    />
                  )}
                  {currentResourceType.welcomeEmail !== undefined && (
                    <Chip 
                      label={`Welcome Email: ${currentResourceType.welcomeEmail ? 'Enabled' : 'Disabled'}`} 
                      size="small" 
                      variant="outlined"
                      color={currentResourceType.welcomeEmail ? 'success' : 'default'}
                    />
                  )}
                  {currentResourceType.allowRating !== undefined && (
                    <Chip 
                      label={`Rating: ${currentResourceType.allowRating ? 'Enabled' : 'Disabled'}`} 
                      size="small" 
                      variant="outlined"
                      color={currentResourceType.allowRating ? 'success' : 'default'}
                    />
                  )}
                </Box>
              </Box>
            )}
          </FormControl>
        </CardContent>
      </Card>

      {/* Data Entry Form */}
      {currentResourceType && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add {currentResourceType.name} Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {currentResourceType.description}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {currentResourceType.fields.map((field) => (
                <Grid item xs={12} md={6} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={loading}
              >
                Reset
              </Button>
              <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE} disableInsteadOfHide={true}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Data'}
                </Button>
              </PermissionWrapper>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {!currentResourceType && resourceTypes.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Save sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            Ready to Add Resource
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Select a resource type from the dropdown above to start adding data entries.
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
              <Typography variant="subtitle2">How to add resource data:</Typography>
              <Typography variant="body2">
                1. Select a resource type from the dropdown above<br />
                2. Fill in the required fields for that resource type<br />
                3. Click "Save Data" to store the information
              </Typography>
            </Alert>
          </Box>
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default ResourceDataManager;
